import React, { useState } from 'react';
import styled from 'styled-components';
import { ContentItem as ContentItemType } from '../../types/content';

interface ContentItemProps {
  content: ContentItemType;
}

const ItemContainer = styled.div`
  display: flex;
  background-color: #1A1427;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  margin-bottom: 12px;
  width: 100%;
  
  &:hover {
    .edit-button {
      opacity: 1;
    }
  }
`;

const Thumbnail = styled.div`
  width: 180px;
  height: 100px;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
`;

const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Duration = styled.div`
  position: absolute;
  bottom: 8px;
  right: 8px;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
`;

const ContentInfo = styled.div`
  flex: 1;
  padding: 16px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  height: 100px;
  position: relative;
`;

const ContentHeader = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 8px;
`;

const BrandTag = styled.div<{ status: string }>`
  display: flex;
  align-items: center;
`;

const BrandLogo = styled.div`
  width: 24px;
  height: 24px;
  background-color: #FF5722;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 14px;
  margin-right: 8px;
  flex-shrink: 0;
`;

const BrandName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #fff;
  margin-right: 4px;
`;

const StatusTag = styled.span<{ status: string }>`
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 4px;
  margin-left: 8px;
  
  ${({ status }) => {
    switch (status.toLowerCase()) {
      case 'live':
        return 'background-color: rgba(46, 204, 113, 0.2); color: #2ecc71;';
      case 'pending':
        return 'background-color: rgba(241, 196, 15, 0.2); color: #f1c40f;';
      case 'revise':
        return 'background-color: rgba(52, 152, 219, 0.2); color: #3498db;';
      case 'rejected':
        return 'background-color: rgba(231, 76, 60, 0.2); color: #e74c3c;';
      default:
        return 'background-color: rgba(255, 255, 255, 0.1); color: #fff;';
    }
  }}
`;

const ContentTitle = styled.h3`
  font-size: 16px;
  font-weight: 500;
  color: #fff;
  margin: 0 0 4px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Description = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
`;

const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
  position: absolute;
  right: 16px;
  top: 16px;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
  
  &:hover {
    color: #fff;
  }
`;

const MoreButton = styled(ActionButton)`
  padding: 8px;
  color: rgba(255, 255, 255, 0.4);
  
  &:hover {
    color: rgba(255, 255, 255, 0.8);
  }
`;

const EditButtonsContainer = styled.div`
  position: absolute;
  bottom: 16px;
  left: 16px;
  display: flex;
  gap: 8px;
`;

const EditButton = styled.button`
  background: none;
  border: none;
  color: #9333EA;
  font-size: 14px;
  padding: 4px 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.2s ease;
  
  svg {
    font-size: 16px;
  }
  
  &:hover {
    color: #A855F7;
  }
`;

const ContentItem: React.FC<ContentItemProps> = ({ content }) => {
  const { title, description, thumbnailUrl, duration, status, brand } = content;
  
  const capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };
  
  const getBrandInitial = (name: string) => {
    return name.charAt(0).toUpperCase();
  };
  
  const renderStatusTag = (status: string) => {
    return <StatusTag status={status}>{capitalizeFirstLetter(status)}</StatusTag>;
  };
  
  const showEditButtons = status.toLowerCase() === 'live';
  const [isHovered, setIsHovered] = useState(false);
  
  // Determine if we should show description based on status
  // Only show description for non-live content
  const showDescription = status.toLowerCase() !== 'live';
  
  return (
    <ItemContainer 
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Thumbnail>
        <ThumbnailImage src={thumbnailUrl} alt={title} />
        <Duration>{duration}</Duration>
      </Thumbnail>
      
      <ContentInfo>
        <ContentHeader>
          <BrandTag status={status}>
            <BrandLogo>
              {brand.logo ? (
                <img src={brand.logo} alt={brand.name} />
              ) : (
                getBrandInitial(brand.name)
              )}
            </BrandLogo>
            <BrandName>{brand.name}</BrandName>
            {renderStatusTag(status)}
          </BrandTag>
        </ContentHeader>
        
        <ContentTitle>{title}</ContentTitle>
        {showDescription && <Description>{description}</Description>}
        
        {showEditButtons && (
          <EditButtonsContainer>
            <EditButton className="edit-button">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
              </svg>
              Edit
            </EditButton>
          </EditButtonsContainer>
        )}
      </ContentInfo>
      
      <ActionsContainer>
        <MoreButton>
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="1"></circle>
            <circle cx="12" cy="5" r="1"></circle>
            <circle cx="12" cy="19" r="1"></circle>
          </svg>
        </MoreButton>
      </ActionsContainer>
    </ItemContainer>
  );
};

export default ContentItem;
